/**
 * `querystringsearch` reducer, wrapped to revalidate without going blank.
 *
 * Core clears `items` and `total` on `_PENDING`, so every refetch empties the
 * listing before refilling it. That was invisible while nothing was
 * server-rendered — the page started empty anyway. Once the browse pages SSR
 * their first page of results (see components/Browse/getSearchBlockAsyncData),
 * core's behaviour becomes a visible regression: server HTML shows the rows,
 * hydration keeps them, then the mount effect in `withQuerystringResults`
 * fires and blanks the table until the response lands.
 *
 * So: keep the previous page's rows visible while the next request is in
 * flight (stale-while-revalidate). `loading` stays true and `loaded` stays
 * false, so every spinner and every `hasLoaded` branch behaves exactly as
 * before — only the rows survive. This also removes the same flash from
 * ordinary pagination and facet changes.
 *
 * Registered through `config.addonReducers`, which `store.js` spreads AFTER
 * core's `reducers`, so a same-named entry replaces core's.
 *
 * It ALSO carries two fields core drops. Core's SUCCESS branch rebuilds each
 * subrequest from scratch and keeps only items/total/batching, so the seam's
 * `facets` (Solr facet counts) and `search_meta` (what the seam actually did --
 * mode, applied status group, sort, operator, scope, and a *_defaulted flag for
 * each) were being thrown away before any component could read them. Those are
 * what make a default VISIBLE instead of silent (item 2), drive the status
 * control (item 6), label the sort (item 19) and explain a zero-result page
 * (item 18), so they have to survive the reducer.
 *
 * A FAILED REFETCH KEEPS THE LAST GOOD RESULT (2026-09-27)
 *
 * Core's `_FAIL` branch rebuilds the subrequest as `{items: [], total: 0}`. A
 * failure is not a measurement, but everything downstream read it as one: the
 * table emptied and BrowseSEO emitted `noindex,follow` for "0 documents". That
 * is exactly what every rendering crawler saw from 2026-09-20, when robots.txt
 * began Disallowing `/++api++/` and the hydration refetch of an SSR-seeded
 * page could no longer be made — so every hub and `?preset=` page on all three
 * sites rendered as noindex for Google and Bing while serving index,follow and
 * full rows to curl. So when a subrequest already holds a successful result,
 * a failure now leaves that result in place (`loaded` stays true) and records
 * the error beside it as `refreshError`, which no core consumer reads. A
 * subrequest with no earlier success fails exactly as core has it, with
 * `error` set, which BrowseSEO treats as "not measured" rather than as zero.
 *
 * The trade-off: a visitor whose facet change fails keeps seeing the rows of
 * the previous query instead of an empty table claiming there are no results.
 * Neither is right; the stale rows are the less misleading of the two.
 *
 * Every success also records `queryKey` (helpers/querystringKey), so the mount
 * effect in withQuerystringResults can tell that the SSR seed already answered
 * the request it was about to repeat, and skip it.
 */
import querystringsearch from '@plone/volto/reducers/querystringsearch/querystringsearch';

const PENDING = 'GET_QUERYSTRING_RESULTS_PENDING';
const SUCCESS = 'GET_QUERYSTRING_RESULTS_SUCCESS';
const FAIL = 'GET_QUERYSTRING_RESULTS_FAIL';

const carried = (result, queryKey) => ({
  facets: result?.facets || {},
  search_meta: result?.search_meta || null,
  queryKey: queryKey || null,
  refreshError: null,
  measured: true,
});

export default function querystringsearchKeepingStale(state, action = {}) {
  if (action.type === FAIL && action.subrequest) {
    // `state` here is the post-PENDING subrequest: core's PENDING spreads the
    // previous entry, so `measured`, `queryKey`, `facets` and `search_meta`
    // from the last success are still on it, and the PENDING branch below
    // kept its rows and total.
    const pending = state?.subrequests?.[action.subrequest];
    if (pending?.measured) {
      return {
        ...state,
        subrequests: {
          ...state.subrequests,
          [action.subrequest]: {
            ...pending,
            loading: false,
            loaded: true,
            refreshError: action.error || true,
          },
        },
      };
    }
  }

  const next = querystringsearch(state, action);

  if (action.type === SUCCESS) {
    const extra = carried(action.result, action.queryKey);
    return action.subrequest
      ? {
          ...next,
          subrequests: {
            ...next.subrequests,
            [action.subrequest]: {
              ...next.subrequests[action.subrequest],
              ...extra,
            },
          },
        }
      : { ...next, ...extra };
  }

  if (action.type !== PENDING || !action.subrequest) return next;

  const previous = state?.subrequests?.[action.subrequest];
  if (!previous?.loaded || !previous.items?.length) return next;

  return {
    ...next,
    subrequests: {
      ...next.subrequests,
      [action.subrequest]: {
        ...next.subrequests[action.subrequest],
        items: previous.items,
        total: previous.total,
        batching: previous.batching,
        // keep the last known meta/facets alongside the stale rows, so the
        // controls do not flicker back to their defaults mid-request
        facets: previous.facets,
        search_meta: previous.search_meta,
      },
    },
  };
}
